import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  StyleSheet,
  Image,
  ImageBackground,
  Text,
  useWindowDimensions,
  View,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { ProgressButton } from '../../helperComponents/ProgressButton';
import { AssessmentIcon } from '../../../resources/svgComponents/AssessmentIcon';
import { SurveyIcon } from '../../../resources/svgComponents/SurveryIcon';
import { LevelIcon } from '../../../resources/svgComponents/LevelIcon';
import getJourneyStages, {
  EStageId,
  getStageRoute,
  getPathLBackgroundLine,
  getPathLBackgroundLineMobile,
  getStageStatus,
  getStageStatusStyle,
  TStage,
  TStageType,
} from '../../../utils/journeysUtils';
import {
  backgroundOperationImages,
  GAME_TYPES_CONFIG,
  GAME_TYPES_TYPE,
} from '../../../constants/constants';
import { BigRadio } from '../../helperComponents/BigRadio';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import { DropdownMenu } from '../../helperComponents/DropdownMenu/DropdownMenu';
import { applyTransparency } from '../../../constants/colors';
import JourneyProgress from '../../helperComponents/JourneyProgress';
import LabelCountBox from '../../helperComponents/LabelCountBox';
import NectarNook from '../../helperComponents/NectarNook';
import JourneyModal from './JourneyModal';
import { PopupV2 } from '../../helperComponents/PopupV2';
import ButtonGeneral from '../../helperComponents/ButtonGeneral';
import { useJourneyQuery } from '../../../graphql/journey/getJourney';
import { useSignedInContext } from '../../../context/SignedInContext';
import { useCreateJourneyMutation } from '../../../graphql/journey/useCreateJourneyMutation.gql';
import { BackButtonV2 } from '../../helperComponents/BackButtonV2';
import { useFocusEffect } from '@react-navigation/native';
import StreakModal from '../../helperComponents/StreakModal';
import { useGetHivesQuery } from '../MyHives/useGetHivesQuery.gql';
import { isMobile } from '@mightybyte/rnw.utils.device-info';
import { TooltipSequence } from '../../helperComponents/ToolTip/ToolTipSequence';

const beeRight = require('../../../resources/images/backgroundBeeRightv2.png');

// Fixed sizes
const CONTAINER_WIDTH = 1165;
const CONTAINER_HEIGHT = 421;
const CONTAINER_WIDTH_MOBILE = 335;
const CONTAINER_HEIGHT_MOBILE = 560;
const PATH_WIDTH = 1000;
const PATH_HEIGHT = 500;
const PATH_WIDTH_MOBILE = 326;
const PATH_HEIGHT_MOBILE = 460;

const getIcon = (type: TStageType) => {
  switch (type) {
    case 'Assessment':
      return AssessmentIcon;
    case 'Survey':
      return SurveyIcon;
    case 'Level':
      return LevelIcon;
    default:
      return null;
  }
};

const getProgressButtonPosition = (stageId: EStageId) => {
  switch (stageId) {
    case EStageId.Assessment1:
      return { top: 85, left: 0 };
    case EStageId.Survey1:
      return { top: 85, left: 300 };
    case EStageId.Level1:
      return { top: 160, left: 545 };
    case EStageId.Level2:
      return { top: 225, left: 300 };
    case EStageId.Level3:
      return { top: 300, left: 125 };
    case EStageId.Assessment2:
      return { top: 390, left: 250 };
    case EStageId.Level4:
      return { top: 390, left: 490 };
    case EStageId.Survey2:
      return { top: 390, left: 660 };
    case EStageId.Assessment3:
      return { top: 390, left: 828.7 };
    default:
      return { top: 0, left: 0 };
  }
};

const getProgressButtonPositionMobile = (stageId: EStageId) => {
  switch (stageId) {
    case EStageId.Assessment1:
      return { top: -10, left: 46 };
    case EStageId.Survey1:
      return { top: 40, left: 185 };
    case EStageId.Level1:
      return { top: 110, left: 130 };
    case EStageId.Level2:
      return { top: 160, left: 12 };
    case EStageId.Level3:
      return { top: 210, left: 130 };
    case EStageId.Assessment2:
      return { top: 260, left: 150 };
    case EStageId.Level4:
      return { top: 340, left: 130 };
    case EStageId.Survey2:
      return { top: 390, left: 6 };
    case EStageId.Assessment3:
      return { top: 460, left: 192 };
    default:
      return { top: 0, left: 0 };
  }
};

export default function ProgressJourney({ navigation, route }) {
  const { stages, totalStages } = getJourneyStages();
  const { gameType } = route.params || {};
  const { refetchStreakHistory } = useSignedInContext();

  // to refetch streaks after games, surverys, assessments
  useFocusEffect(
    useCallback(() => {
      refetchStreakHistory();
    }, [refetchStreakHistory]),
  );

  const { width } = useWindowDimensions();
  const pathSizeLimit = width < 1161;

  const operation = GAME_TYPES_CONFIG[gameType as GAME_TYPES_TYPE].operation;
  const BackgroundOperationImage = backgroundOperationImages[operation];

  const [showLevelGameModal, setShowLevelGameModal] = useState(false);

  const [levelGameModalDetails, setLevelGameModalDetails] = useState<TStage | undefined>();

  const { error: getHivesError, data: getHivesData } = useGetHivesQuery();
  // const hiveId = getHivesData?.hives?.[0]?.id ?? '';

  const android = Platform.OS === 'android';

  const mobile = Platform.OS !== 'web';

  const stageRoutePress = (
    stageRoute: string,
    stageId: EStageId,
    stageGameType: GAME_TYPES_TYPE,
    journeyId: string,
  ) => {
    const currentParams = route.params || {};
    navigation.navigate(stageRoute, {
      ...currentParams,
      stageId,
      gameType: stageGameType,
      journey: true,
      journeyId,
    });
  };
  //TODO: fetch backend

  const { user } = useSignedInContext();
  const userId = user?.id;
  // Queries & Mutations
  const {
    data,
    loading: loadingQuery,
    error,
    refetch,
  } = useJourneyQuery({
    variables: { userId: userId ?? '', gameType: gameType ?? '' },
    skip: !userId || !gameType,
  });

  const nectarCount = user?.points ?? 0;

  // ✅ refetch when screen gains focus
  useFocusEffect(
    useCallback(() => {
      if (userId && gameType) {
        refetch();
      }
    }, [userId, gameType, refetch]),
  );

  const [createJourney] = useCreateJourneyMutation();

  // Ensure journey exists
  useEffect(() => {
    if (!loadingQuery && userId && gameType) {
      if (!data?.journeyByUserAndGametype) {
        createJourney({
          variables: {
            data: {
              currentStage: EStageId.Start,
              userId,
              gameType,
            },
          },
        }).then(() => {
          refetch();
        });
      }
    }
  }, [loadingQuery, userId, gameType, data, createJourney, refetch]);

  const journeyData = data?.journeyByUserAndGametype ?? null;

  const currentStage = loadingQuery
    ? stages[0]
    : stages.find(stage => stage.id === journeyData?.currentStage) || stages[0];

  const index = stages.findIndex(stage => stage.id === journeyData?.currentStage);

  const journeyProgress =
    journeyData && index !== -1 ? Math.ceil((100 * index) / (totalStages - 1)) : 0;

  const isMobile = Platform.OS !== 'web';

  const onPressProgressButton = ({ stageRoute, stage }: { stageRoute: string; stage: TStage }) => {
    if (stage.type === 'Level') {
      setShowLevelGameModal(true);
      setLevelGameModalDetails(stage);
      return;
    }
    stageRoutePress(stageRoute, stage.id, gameType, journeyData?._id);
  };

  const handleStartOrContinue = () => {
    if (!journeyData) return;

    // Find current stage index
    const currentIndex = stages.findIndex(stage => stage.id === currentStage.id);

    // Determine next stage (fallback to current if at end)
    const nextStage = stages[currentIndex + 1] || currentStage;

    // If next stage is a Level, show modal
    if (nextStage.type === 'Level') {
      setLevelGameModalDetails(nextStage);
      setShowLevelGameModal(true);
      return;
    }

    // Otherwise, navigate to the next stage
    const stageRoute = getStageRoute(nextStage.type);
    stageRoutePress(stageRoute, nextStage.id, gameType, journeyData._id);
  };

  // for loading
  if (!journeyData || loadingQuery) {
    return (
      <ComponentWrapper hasInset hasScroll={true} hideBee={true}>
        <DropdownMenu
          backButton={
            <BackButtonV2
              onPress={() => {
                navigation.navigate('SelectGameType', {
                  journey: true,
                  userName: userId,
                });
              }}
            />
          }
          top={20}
        />
        <View
          style={{ height: '100%', justifyContent: 'center', alignItems: 'center', marginTop: 100 }}
        >
          <ActivityIndicator size={'large'} color="#E89823" />
        </View>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper hasInset hasScroll={true} hideBee={true}>
      <DropdownMenu
        backButton={
          <BackButtonV2
            onPress={() => {
              navigation.navigate('SelectGameType', {
                journey: true,
                userName: userId,
              });
            }}
          />
        }
        top={20}
      />
      {!pathSizeLimit && (
        <View style={[styles.nectarButtonsContainer]}>
          <LabelCountBox countText={String(nectarCount)} />
          <NectarNook navigation={navigation} />
        </View>
      )}
      {!pathSizeLimit && (
        <View style={[styles.mainContainer]}>
          <Text style={styles.title}>
            {GAME_TYPES_CONFIG[gameType as GAME_TYPES_TYPE].operation}
          </Text>
          <View
            style={[
              styles.container,
              {
                width: pathSizeLimit ? CONTAINER_WIDTH_MOBILE : CONTAINER_WIDTH,
                height: pathSizeLimit ? CONTAINER_HEIGHT_MOBILE : CONTAINER_HEIGHT,
              },
            ]}
          >
            <View style={styles.operandImageContainer}>
              <BackgroundOperationImage color={'#FFFFFF'} width={393} height={368} />
            </View>

            <Image
              resizeMode="cover"
              style={[styles.beeRight, isMobile && { bottom: -90, right: 50 }]}
              source={beeRight}
            />

            <View style={styles.buttonContainer}>
              <BigRadio gameType={gameType} onPress={() => {}} progress={journeyProgress} />
            </View>

            <View style={styles.pathWrapper}>
              <ImageBackground
                source={getPathLBackgroundLine(currentStage.id)}
                resizeMode="contain"
                style={{
                  width: PATH_WIDTH,
                  height: PATH_HEIGHT,
                }}
              >
                {stages.map((stage: TStage) => {
                  const Icon = getIcon(stage.type);
                  const stageStatus = getStageStatus(stage, currentStage.stage);
                  const stageStyle = getStageStatusStyle(stageStatus);
                  const basePos = getProgressButtonPosition(stage.id);
                  const stageRoute = getStageRoute(stage.type);

                  if (stage.id === EStageId.Start) {
                    return null;
                  }

                  return (
                    <View
                      style={{
                        position: 'absolute',
                        top: basePos.top,
                        left: basePos.left,
                      }}
                      key={stage.id}
                    >
                      <ProgressButton
                        title={stage.name}
                        icon={
                          Icon ? (
                            <Icon
                              backColor={stageStyle.backColorIcon}
                              color={stageStyle.colorIcon}
                            />
                          ) : null
                        }
                        dotPosition={(stage as any).dotPosition || 'right'}
                        colorText={stageStyle.colorText}
                        backgroundColor={stageStyle.buttonBackground}
                        dotColor={stageStyle.dotColor}
                        onPress={() => {
                          if (stageStatus === 'locked' || stageStatus === 'done') {
                            return;
                          }
                          onPressProgressButton({ stageRoute, stage });
                        }}
                      />
                    </View>
                  );
                })}
              </ImageBackground>
            </View>
          </View>
          <View style={[styles.startButtonContainer]}>
            <ButtonGeneral
              onPress={handleStartOrContinue}
              title={currentStage.id === EStageId.Start ? 'Start Journey' : 'Continue Journey'}
            />
            <TooltipSequence
              stage={4}
              backgroundColor="rgba(240, 236, 7, 0.25)"
              borderColor="rgba(255, 255, 255, 0.4)"
              borderWidth={5}
            />
          </View>
        </View>
      )}

      {pathSizeLimit && (
        <View style={[styles.mainContainerMobile]}>
          <View
            style={[
              styles.statusContainerMobile,
              mobile && { marginTop: Platform.OS === 'android' ? 90 : 45 },
              android && { width: '100%' },
            ]}
          >
            <JourneyProgress gameType={gameType} progress={journeyProgress} />
            <View style={[styles.nectarButtonsContainerMobile]}>
              <LabelCountBox
                borderColor1="#FFF5DD"
                borderColor2="#FFF5DD"
                width={159.18}
                height={51.77}
                gradientBorderHeight={56}
                backgroundColor="#FCEABB"
                backgroundColorCount="#FEDD88"
                countText={String(nectarCount)}
              />
              <NectarNook
                borderColor1="#FFF5DD"
                borderColor2="#FFF5DD"
                width={159.18}
                height={51.77}
                gradientBorderHeight={56}
                backgroundColor="#FCEABB"
                backgroundColorIcon="#FEDD88"
                navigation={navigation}
              />
            </View>
          </View>

          <View
            style={[
              styles.containerMobile,
              {
                width: CONTAINER_WIDTH_MOBILE,
                height: CONTAINER_HEIGHT_MOBILE,
              },
              mobile && { top: Platform.OS === 'android' ? 220 : 164 },
            ]}
          >
            <View style={styles.operandImageContainerMobile}>
              <BackgroundOperationImage color={'#FFFFFF'} width={239} height={224} />
            </View>
            <Image resizeMode="cover" style={[styles.beeRightMobile]} source={beeRight} />
            <View style={styles.pathWrapper}>
              <ImageBackground
                source={getPathLBackgroundLineMobile(currentStage.id)}
                resizeMode="contain"
                style={{
                  width: PATH_WIDTH_MOBILE,
                  height: PATH_HEIGHT_MOBILE,
                }}
              >
                {stages.map((stage: TStage) => {
                  const Icon = getIcon(stage.type);
                  const stageStatus = getStageStatus(stage, currentStage.stage);
                  const stageStyle = getStageStatusStyle(stageStatus);
                  const basePos = getProgressButtonPositionMobile(stage.id);
                  const stageRoute = getStageRoute(stage.type);

                  if (stage.id === EStageId.Start) {
                    return null;
                  }

                  return (
                    <View
                      style={{
                        position: 'absolute',
                        top: basePos.top,
                        left: basePos.left,
                      }}
                      key={stage.id}
                    >
                      <ProgressButton
                        title={stage.name}
                        height={34.47}
                        icon={
                          Icon ? (
                            <Icon
                              backColor={stageStyle.backColorIcon}
                              color={stageStyle.colorIcon}
                              width={24}
                              height={24}
                            />
                          ) : null
                        }
                        dotPosition={(stage as any).dotPositionMobile || 'right'}
                        colorText={stageStyle.colorText}
                        backgroundColor={stageStyle.buttonBackground}
                        dotColor={stageStyle.dotColor}
                        dotSize={19}
                        fontSize={12}
                        onPress={() => {
                          if (stageStatus === 'locked' || stageStatus === 'done') {
                            return;
                          }
                          onPressProgressButton({ stageRoute, stage });
                        }}
                      />
                    </View>
                  );
                })}
              </ImageBackground>
              <View style={[styles.startButtonContainerMobile]}>
                <ButtonGeneral
                  fontSize={14}
                  width={201}
                  height={49}
                  onPress={handleStartOrContinue}
                  title={currentStage.id === EStageId.Start ? 'Start Journey' : 'Continue Journey'}
                />
                <TooltipSequence
                  stage={4}
                  backgroundColor="rgba(240, 236, 7, 0.25)"
                  borderColor="rgba(255, 255, 255, 0.4)"
                  borderWidth={5}
                />
              </View>
            </View>
          </View>
        </View>
      )}
      <PopupV2
        title="Practice"
        visible={showLevelGameModal}
        setVisible={setShowLevelGameModal}
        height={isMobile ? 472 : 534}
        hexagonHeight={isMobile ? 54 : 62}
        hexagonWidth={isMobile ? 252 : 346}
      >
        <JourneyModal
          stage={levelGameModalDetails}
          onClose={() => setShowLevelGameModal(false)}
          gameType={gameType}
          onProceed={() => {
            setShowLevelGameModal(false);
          }}
          navigation={navigation}
          route={route}
          journeyId={journeyData?._id}
        />
      </PopupV2>

      <StreakModal />
    </ComponentWrapper>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 20,
    backgroundColor: '#FFF5DD',
    position: 'relative',
    borderRadius: 16,
    borderWidth: 3,
    borderColor: '#FEDD88',
    overflow: 'hidden',
  },
  containerMobile: {
    backgroundColor: '#FFF5DD',
    position: 'relative',
    borderRadius: 16,
    paddingTop: 20,
    border: '3px solid #FEDD88',
    top: 250,
  },
  buttonContainer: {
    width: 344,
    position: 'absolute',
    right: 20,
  },
  buttonContainerMobile: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  pathWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pathWrapperMobile: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  beeRight: {
    width: 170,
    height: 171.27,
    position: 'absolute',
    bottom: '20%',
    right: '5%',
  },
  beeRightMobile: {
    width: 100,
    height: 100,
    position: 'absolute',
    bottom: '20%',
    right: '2%',
  },
  operandImageContainer: {
    position: 'absolute',
    top: -20,
    right: -80,
  },
  operandImageContainerMobile: {
    position: 'absolute',
    top: 80,
    right: 30,
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginBottom: 20,
  },
  startButtonContainer: {
    marginTop: 22,
    position: 'relative',
  },
  startButtonContainerMobile: {
    marginTop: 30,
    position: 'relative',
  },

  statusContainerMobile: {
    backgroundColor: '#FEDD88',
    marginTop: 100,
    width: 374,
    height: 181,
    position: 'absolute',
  },
  mainContainer: {
    alignItems: 'center',
    marginTop: 200,
  },
  mainContainerMobile: {
    alignItems: 'center',
    paddingHorizontal: 20,
    height: '100%',
    minHeight: 1200,
  },
  nectarButtonsContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,
  },
  nectarButtonsContainerMobile: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    gap: 20,
  },
});
